import { useState } from "react";
import { toast } from "sonner";
import { PublicShell } from "@/components/PublicShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { api, formatApiError } from "@/lib/api";
import { Users, Store, HeartHandshake, Loader2, Send, Search, AlertTriangle } from "lucide-react";

const categories = [
  { v: "beneficiary", t: "Beneficiary Issue", icon: Users },
  { v: "vendor", t: "Vendor Issue", icon: Store },
  { v: "sponsor_programme", t: "Sponsor / Programme Issue", icon: HeartHandshake },
];

const statusColor = (s) => ({
  open: "bg-amber-100 text-amber-800",
  in_review: "bg-blue-100 text-blue-800",
  resolved: "bg-emerald-100 text-emerald-800",
  closed: "bg-gray-200 text-gray-700",
}[s] || "bg-gray-200 text-gray-700");

export default function Complaints() {
  const [cat, setCat] = useState("beneficiary");
  const [f, setF] = useState({ name: "", phone: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [ticket, setTicket] = useState(null);
  const [lookup, setLookup] = useState("");
  const [lookingUp, setLookingUp] = useState(false);
  const [found, setFound] = useState(null);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/complaints", { category: cat, ...f });
      setTicket(data.ticket);
      toast.success(`Ticket created · ${data.ticket}`);
      setF({ name: "", phone: "", email: "", subject: "", message: "" });
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  const checkStatus = async (e) => {
    e.preventDefault();
    if (!lookup.trim()) return;
    setLookingUp(true); setFound(null);
    try {
      const { data } = await api.get(`/complaints/${lookup.trim()}`);
      setFound(data.complaint);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLookingUp(false); }
  };

  return (
    <PublicShell>
      <section className="bg-emerald-950 tmf-grain">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16 lg:py-20">
          <div className="flex items-center gap-3"><AlertTriangle className="w-7 h-7 text-amber-300" /><span className="text-xs font-mono uppercase tracking-widest text-emerald-300">Complaints & Dispute Resolution</span></div>
          <h1 className="mt-3 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight max-w-3xl">Report a Problem</h1>
          <p className="mt-4 text-emerald-200/80 max-w-2xl">Raise an issue and get a tracking ticket. Every complaint is logged, reviewed and resolved transparently.</p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 py-16 lg:py-24 grid lg:grid-cols-5 gap-10 items-start">
        {/* REPORT */}
        <div className="lg:col-span-3">
          {ticket ? (
            <div data-testid="complaint-success" className="rounded-3xl border border-emerald-200 bg-white p-8 text-center">
              <div className="w-16 h-16 rounded-2xl bg-emerald-100 flex items-center justify-center mx-auto"><AlertTriangle className="w-8 h-8 text-emerald-600" /></div>
              <h2 className="mt-5 font-display font-bold text-2xl text-emerald-950">Complaint Logged</h2>
              <p className="mt-2 text-sm text-gray-600">Save this ticket number to check your complaint status.</p>
              <div data-testid="complaint-ticket" className="mt-4 font-mono font-bold text-lg text-emerald-700 bg-emerald-50 px-4 py-2 rounded-xl inline-block">{ticket}</div>
              <div className="mt-6"><Button data-testid="new-complaint-btn" variant="outline" onClick={() => setTicket(null)} className="rounded-full">Report another issue</Button></div>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="complaint-form" className="rounded-3xl border border-emerald-100 bg-white p-6 sm:p-8 space-y-5">
              <div>
                <Label className="text-base">Category</Label>
                <div className="mt-3 grid sm:grid-cols-3 gap-3">
                  {categories.map((c) => (
                    <button key={c.v} type="button" data-testid={`complaint-cat-${c.v}`} onClick={() => setCat(c.v)}
                      className={`rounded-xl border-2 p-4 text-left transition-all ${cat === c.v ? "border-emerald-500 bg-emerald-50" : "border-gray-200 hover:border-emerald-200"}`}>
                      <c.icon className="w-5 h-5 text-emerald-600" />
                      <div className="mt-2 font-semibold text-xs text-emerald-950">{c.t}</div>
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label>Full Name</Label><Input data-testid="complaint-name" value={f.name} onChange={(e) => set("name", e.target.value)} required className="mt-1.5 h-11" /></div>
                <div><Label>Phone</Label><Input data-testid="complaint-phone" value={f.phone} onChange={(e) => set("phone", e.target.value)} className="mt-1.5 h-11" /></div>
              </div>
              <div><Label>Email</Label><Input data-testid="complaint-email" type="email" value={f.email} onChange={(e) => set("email", e.target.value)} className="mt-1.5 h-11" /></div>
              <div><Label>Subject</Label><Input data-testid="complaint-subject" value={f.subject} onChange={(e) => set("subject", e.target.value)} required className="mt-1.5 h-11" /></div>
              <div><Label>Describe the issue</Label><Textarea data-testid="complaint-message" value={f.message} onChange={(e) => set("message", e.target.value)} required rows={5} className="mt-1.5" /></div>
              <Button data-testid="complaint-submit" type="submit" disabled={loading} className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 rounded-full">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Submit Complaint <Send className="w-4 h-4 ml-2" /></>}
              </Button>
            </form>
          )}
        </div>

        {/* CHECK STATUS */}
        <div className="lg:col-span-2">
          <div className="rounded-3xl border border-emerald-100 bg-emerald-50/60 p-6 sm:p-8">
            <h3 className="font-display font-bold text-lg text-emerald-950">Check Complaint Status</h3>
            <p className="mt-2 text-sm text-gray-600">Enter your ticket number to see the latest status.</p>
            <form onSubmit={checkStatus} className="mt-4 flex gap-2">
              <Input data-testid="complaint-lookup" value={lookup} onChange={(e) => setLookup(e.target.value.toUpperCase())} placeholder="TMF-CMP-000000" className="h-11 font-mono" />
              <Button data-testid="complaint-lookup-btn" type="submit" disabled={lookingUp} className="h-11 bg-emerald-600 hover:bg-emerald-700 shrink-0">{lookingUp ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}</Button>
            </form>
            {found && (
              <div data-testid="complaint-status-result" className="mt-5 rounded-2xl bg-white border border-emerald-100 p-5">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-sm text-emerald-700">{found.ticket}</span>
                  <Badge className={statusColor(found.status)}>{String(found.status).replace("_", " ")}</Badge>
                </div>
                <div className="mt-2 font-semibold text-emerald-950 text-sm">{found.subject}</div>
                <div className="mt-3 space-y-2">
                  {(found.history || []).map((h, i) => (
                    <div key={`${h.at}-${i}`} className="text-xs text-gray-600 flex justify-between gap-3 border-t border-emerald-50 pt-2">
                      <span className="capitalize">{String(h.status).replace("_", " ")}{h.note ? ` — ${h.note}` : ""}</span>
                      <span className="text-gray-400 whitespace-nowrap">{new Date(h.at).toLocaleDateString()}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </PublicShell>
  );
}
